import React, { FC, useCallback, useState } from 'react';
import { WorkspaceBadge, Skeleton, notification, Tooltip } from 'src/common';
import { ILinkedBid } from 'src/models/bids/BidsSelector/types';
import {
  useAssignMailsToBid,
  useUnAssignMailsToBid,
  updateCacheOnAssignCallOffToBid
} from 'src/models/bids/BidsSelector/hooks';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';
import { trackConnectItemsToBid } from 'src/segment/events';
import { BidItemType } from '@tendium/prom-types/tender';
import { IBidIdentifier, ILinkedBidIdentifier } from 'src/models/bids/Bids/types';
import ConfirmModal from '../ConfirmModal';

interface IProps {
  item: ILinkedBid;
  onFinish?: () => void;
  onCloseDropDown: () => void;
  linkedBids: ILinkedBidIdentifier[];
  origins: string[];
  bids: IBidIdentifier[];
  eventSource: string;
  setIsUpdating: (val: boolean) => void;
}

const BidPickerItem: FC<IProps> = props => {
  const { item, onFinish, linkedBids, origins, bids, setIsUpdating, eventSource, onCloseDropDown } = props;
  const { workspace, name } = item;
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const showModal = useCallback(() => setModalVisible(true), []);
  const hideModal = useCallback(() => setModalVisible(false), []);
  const [assignMailsToBid, { loading: assignLoading }] = useAssignMailsToBid();
  const [unassignMailsToBid, { loading: unassignLoading }] = useUnAssignMailsToBid();

  const onFinishAssigning = useCallback(() => {
    setIsUpdating(false);
    onFinish?.();
    onCloseDropDown();
  }, [onCloseDropDown, onFinish, setIsUpdating]);

  const onAssign = useCallback(async () => {
    try {
      setIsUpdating(true);
      if (!origins.length) {
        hideModal();
        setIsUpdating(false);
        onFinish?.();
        return;
      }

      const groups = groupBy(linkedBids, 'id');

      const promises = Object.entries(groups).map(([key, val]) => {
        return unassignMailsToBid({
          variables: { bidId: key, mailIds: val.map(bid => bid.originId) }
        });
      });

      await Promise.all(promises);

      await assignMailsToBid({
        variables: {
          bidId: item.id,
          mailIds: origins
        },
        update: updateCacheOnAssignCallOffToBid(origins)
      });
      hideModal();
      trackConnectItemsToBid(origins, BidItemType.CallOff, eventSource, item.id);
      onFinishAssigning();
    } catch {
      notification.error({
        message: t('Common.unknownError'),
        description: t('Common.unknownErrorDesc')
      });
    }
  }, [
    assignMailsToBid,
    eventSource,
    hideModal,
    item.id,
    linkedBids,
    onFinish,
    onFinishAssigning,
    origins,
    setIsUpdating,
    t,
    unassignMailsToBid
  ]);

  const onClick = useCallback(() => {
    if (!!bids.length || !!linkedBids.length) {
      onCloseDropDown();
      showModal();
    } else {
      onAssign();
    }
  }, [bids.length, linkedBids.length, onAssign, onCloseDropDown, showModal]);

  const loading = assignLoading || unassignLoading;

  return (
    <div className={styles.item}>
      <button onClick={onClick} className={styles.button}>
        <Tooltip title={name} className={styles.tooltip}>
          <WorkspaceBadge title={workspace.name} color={workspace.color} mode={'short'} className={styles.wsBadge} />
          <span className={styles.label}>{name}</span>
        </Tooltip>
      </button>
      <Skeleton className={styles.itemLoading} loading={loading} paragraph={false} active={loading} />
      {modalVisible && <ConfirmModal onOk={onAssign} onCancel={hideModal} linkedBids={linkedBids} bids={bids} />}
    </div>
  );
};

export default BidPickerItem;
